/**
 * Session d'édition avancée d'un exercice : sauvegarde automatique au blur
 * des champs, directement sur l'exercice (un admin corrige le contenu sans
 * circuit de validation, lot 1b).
 */
import { useState, useCallback } from 'react';
import ExercicesApiService from '../../../services/exercicesApi';
import { Exercice } from '../../../types';
import { AppEnvironment } from '../../../config/environment';
import { toUserMessage } from '../../../lib/http';
import { logger } from '../../../lib/logger';

interface EditorAutoSaveParams {
  id: string | undefined;
  environment: AppEnvironment;
  isReadOnly: boolean;
}

/** Auto-save au blur des champs, avec son état (en cours, enregistré, erreur). */
export function useEditorAutoSave({ id, environment, isReadOnly }: EditorAutoSaveParams) {
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const clearAutoSaveError = useCallback(() => setAutoSaveError(null), []);

  const handleFieldBlur = useCallback(async (field: string, _value: unknown, allFormData: Omit<Exercice, 'id'>) => {
    if (isReadOnly || !id) return;
    setAutoSaving(true);
    try {
      await ExercicesApiService.updateExercice(environment, id, allFormData as unknown as Record<string, unknown>);
      setLastSaved(new Date());
      setAutoSaveError(null);
    } catch (err) {
      logger.error('[ExerciceAdvancedEditor][handleFieldBlur] sauvegarde échouée', { id, field, err });
      setAutoSaveError(toUserMessage(err, 'Sauvegarde automatique impossible'));
    } finally {
      setAutoSaving(false);
    }
  }, [isReadOnly, id, environment]);

  return { autoSaving, lastSaved, autoSaveError, clearAutoSaveError, handleFieldBlur };
}
